/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import type { WorkspaceId } from './access.models';

/**
 * Which workspaces reach which destination.
 *
 * <p>One map rather than a guard per route, because the rail reads it too: a destination that is
 * hidden but reachable, or reachable but hidden, is the bug this shape rules out. A path is open
 * to anyone holding at least one of the workspaces listed against it.
 *
 * <p>Keys match a path exactly or as a prefix, so `/admin` covers the whole admin console and
 * `/mmt` covers the repair it deep-links to.
 */
export const ROUTE_WORKSPACES: Record<string, WorkspaceId[]> = {
  '/': ['MANUFACTURING'],
  '/repair-overview': ['MANUFACTURING'],
  '/online': ['MANUFACTURING'],
  '/mmt': ['MANUFACTURING'],
  '/offline': ['MANUFACTURING'],
  '/gsip': ['MANUFACTURING'],
  '/dtc': ['MANUFACTURING'],
  '/diagnostic': ['MANUFACTURING'],
  // Schematics, MECL and the viewer they open belong to both: manufacturing repairs a car with
  // them and engineering designs one with them.
  '/schematic-explorer': ['MANUFACTURING', 'ENGINEERING'],
  '/mecl': ['MANUFACTURING', 'ENGINEERING'],
  '/viewer-3d': ['MANUFACTURING', 'ENGINEERING'],
  // The landing's prefix would cover the capabilities under it, but each is named anyway: the
  // test that holds this map against the router matches keys exactly, so a page added without a
  // line here fails there rather than inheriting a gate nobody chose for it.
  '/engineering-diagnostics': ['ENGINEERING'],
  '/engineering-diagnostics/parts': ['ENGINEERING'],
  '/engineering-diagnostics/failures': ['ENGINEERING'],
  '/engineering-diagnostics/assistant': ['ENGINEERING'],
  '/analytics': ['ANALYTICS'],
  '/admin': ['ADMIN'],
};

/**
 * Paths any provisioned user may reach whatever they hold.
 *
 * <p>Named rather than left to fall through the map, so that a destination added without a
 * workspace is a test failure instead of a quietly open door.
 */
export const UNGATED_ROUTES = ['/resource-links'];

/** Longest first, so a nested entry would answer before the prefix it sits under. */
const MATCH_ORDER = Object.keys(ROUTE_WORKSPACES).sort((a, b) => b.length - a.length);

/** Where each workspace opens, in the order a landing is chosen for someone holding several. */
const LANDINGS: [WorkspaceId, string][] = [
  ['MANUFACTURING', '/'],
  ['ENGINEERING', '/engineering-diagnostics'],
  ['ANALYTICS', '/analytics'],
  ['ADMIN', '/admin'],
];

/** The workspaces that reach `pathname`, or null when it is open to everyone. */
export function workspacesFor(pathname: string): WorkspaceId[] | null {
  if (UNGATED_ROUTES.includes(pathname)) return null;
  const match = MATCH_ORDER.find((path) => pathname === path || pathname.startsWith(`${path}/`));
  return match ? ROUTE_WORKSPACES[match] : null;
}

/** Where someone holding these workspaces should be sent, or null if they hold none. */
export function landingFor(workspaces: WorkspaceId[]): string | null {
  return LANDINGS.find(([workspace]) => workspaces.includes(workspace))?.[1] ?? null;
}
